import { useState, useRef, useEffect, useMemo } from 'react';

/**
 * SearchSelect — dropdown con búsqueda (typeahead).
 *
 * Reemplaza a <select> en campos de catálogo: el Vendedor teclea 2-3
 * letras y filtra. Teclado: ↑/↓ navega, Enter selecciona, Esc cierra.
 *
 * Props:
 *   options     array de objetos
 *   value       valor actual (se compara contra getValue(option))
 *   onChange    (newValue, option) => void
 *   getValue    option => valor (default: o.id)
 *   getLabel    option => texto principal (default: o.name)
 *   getSub      option => texto secundario opcional (default: null)
 *   placeholder texto cuando no hay selección
 *   disabled    bool
 *   allowEmpty  si true, muestra opción "—" para limpiar (default true)
 *   emptyLabel  etiqueta de la opción vacía (default '—  None')
 */
export default function SearchSelect({
  options = [],
  value,
  onChange,
  getValue = o => o.id,
  getLabel = o => o.name,
  getSub = null,
  getImage = null,
  thumbClass = '',
  zoomOnHover = false,
  placeholder = 'Type to search…',
  disabled = false,
  allowEmpty = true,
  emptyLabel = '—  None',
}) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const [hovered, setHover] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(getValue(o)) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => {
      const hay = `${getLabel(o)} ${getSub ? getSub(o) || '' : ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query, getLabel, getSub]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { setHover(0); }, [query, open]);

  const pick = (opt) => {
    onChange(opt == null ? '' : getValue(opt), opt || null);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = e => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true); e.preventDefault(); return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') { setHover(h => Math.min(h + 1, filtered.length - 1)); e.preventDefault(); }
    if (e.key === 'ArrowUp')   { setHover(h => Math.max(h - 1, 0)); e.preventDefault(); }
    if (e.key === 'Enter')     { if (filtered[hovered]) pick(filtered[hovered]); e.preventDefault(); }
    if (e.key === 'Escape')    { setOpen(false); setQuery(''); }
  };

  return (
    <div className="searchselect" ref={wrapRef}>
      <input
        ref={inputRef}
        className={`form-input searchselect-input${getImage && selected && getImage(selected) ? ' has-thumb' : ''}`}
        disabled={disabled}        placeholder={selected ? '' : placeholder}
        value={open ? query : (selected ? getLabel(selected) : '')}
        onFocus={() => {
          if (disabled) return;
          const r = wrapRef.current?.getBoundingClientRect();
          if (r) setDropUp((window.innerHeight - r.bottom) < 280 && r.top > 280);
          setOpen(true);
        }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onMouseDown={() => { if (!disabled && !open) setOpen(true); }}
        onKeyDown={onKeyDown}
        style={selected && !open ? { fontWeight: 600 } : undefined}
      />
      {getImage && selected && !open && getImage(selected) && (
        <img src={getImage(selected)} alt="" className="searchselect-field-thumb" />
      )}
      <span className="searchselect-caret">▼</span>

      {open && !disabled && (
        <div className={`searchselect-menu${dropUp ? ' drop-up' : ''}`}>
          {allowEmpty && !query && (
            <div
              className={`searchselect-option${!selected ? ' selected' : ''}`}
              onMouseDown={e => { e.preventDefault(); pick(null); }}
            >
              {emptyLabel}
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="searchselect-empty">No matches for “{query}”</div>
          ) : filtered.map((o, i) => {
            const isSel = selected && String(getValue(o)) === String(getValue(selected));
            const sub = getSub ? getSub(o) : null;
            return (
              <div
                key={getValue(o)}
                className={`searchselect-option${i === hovered ? ' hovered' : ''}${isSel ? ' selected' : ''}`}
                onMouseEnter={() => { setHover(i); if (zoomOnHover && getImage && getImage(o)) setZoomSrc(getImage(o)); }}
                onMouseLeave={() => { if (zoomOnHover) setZoomSrc(null); }}
                onMouseDown={e => { e.preventDefault(); pick(o); }}
              >
                {getImage && getImage(o) && (
                  <img src={getImage(o)} alt="" className={`searchselect-thumb ${thumbClass}`} />
                )}
                <div className="searchselect-option-body">
                  <div>{getLabel(o)}</div>
                  {sub && <div className="searchselect-option-sub">{sub}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {zoomOnHover && zoomSrc && open && (
        <div className="searchselect-zoom">
          <img src={zoomSrc} alt="" />
        </div>
      )}
    </div>
  );
}
